import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CATEGORICAL_COLORS } from '@/src/config';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { toLabelMap } from '@/src/segmentation/io/import';
import { readImage } from '@/src/io/readWriteImage';
import { useToolStore } from '@/src/store/tools';
import { usePaintToolStore } from '@/src/store/tools/paint';
import { Tools } from '@/src/store/tools/types';
import { PaintMode } from '@/src/core/tools/paint';
import { assetFile } from './api';
import type { DentalAnnotations, DentalAsset, DentalInstance, DentalLandmark } from './types';
import { assertSameGrid, exportVolumeLabels } from './volumeAnnotations';
import { createBrushHistory } from './brushHistory';
import { normalizeAssetGrid } from './assetGrid';

type EditingInstance = DentalInstance & { segmentId: string };
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const useDentalCbctStore = defineStore('dental-cbct', () => {
  const imageId = ref(''); const frameId = ref(''); const instances = ref<EditingInstance[]>([]);
  const landmarks = ref<DentalLandmark[]>([]); const dirty = ref(false); const labelsDirty = ref(false);
  const labelAssetId = ref<string | null>(null); const mode = ref<'navigate' | 'window' | 'paint' | 'erase' | 'select' | 'landmark'>('navigate');
  const landmarkName = ref('Landmark 1');
  const segmentation = useSegmentationStore(); const registry = useSegmentStore().segments;
  function changed() { dirty.value = true; labelsDirty.value = true; }
  const history = createBrushHistory(() => imageId.value, changed);
  const selected = computed(() => instances.value.find(row => row.segmentId === registry.selectedSegmentId.value));
  function select(id: string) { registry.selectSegment(id); }
  function colorAt(index: number): DentalInstance['color'] { return [...CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length], 255] as DentalInstance['color']; }
  function mint(row: DentalInstance) { return registry.mintSegment({ name: registry.uniqueName(row.name), color: row.color, visible: row.visible, locked: row.locked }); }
  async function loadAnnotations(annotations: DentalAnnotations, asset?: DentalAsset) {
    setMode('navigate');
    instances.value.forEach(row => registry.deleteSegment(row.segmentId));
    const byValue = new Map(Object.entries(asset?.labelDefinitions || {}).map(([name, value]) => [value, name]));
    const embedded = new Map(asset?.segmentDefinitions?.map(row => [row.labelValue, row]));
    const rows = annotations.instances.length ? clone(annotations.instances) : (asset?.labelValues || []).map((value, index): DentalInstance => ({
      id: crypto.randomUUID(), labelValue: value, name: byValue.get(value) || `Label ${value}`, fdi: null,
      color: embedded.get(value)?.color || colorAt(index), visible: true, locked: false,
    }));
    instances.value = [];
    if (asset) {
      const decoded = (await readImage(await assetFile(asset))).image;
      normalizeAssetGrid(decoded, asset);
      const parent = useImageCacheStore().getVtkImageData(imageId.value)!;
      assertSameGrid(parent, decoded);
      const { labelmap, excluded } = toLabelMap(decoded);
      if (excluded) throw new Error('Label data contains unsupported values; import was refused.');
      const masks = segmentation.splitLabelmapIntoMasks(imageId.value, labelmap, rows.map(row => ({
        value: row.labelValue, name: row.name, color: row.color, visible: row.visible, locked: row.locked,
      })), { ownSegments: true });
      instances.value = rows.map((row, i) => ({ ...row, segmentId: masks[i].segmentId }));
      // The decoded interchange image is temporary, not another source volume.
      if (labelmap !== decoded) labelmap.delete(); decoded.delete();
    } else instances.value = rows.map(row => ({ ...row, segmentId: mint(row) }));
    landmarks.value = clone(annotations.landmarks); labelAssetId.value = annotations.labelmapAssetId;
    history.clear(); dirty.value = false; labelsDirty.value = false;
    if (instances.value.length) select(instances.value[0].segmentId);
  }
  function addInstance(value: number, name: string, fdi: number | null = null) {
    if (!Number.isInteger(value) || value < 1 || value > 65535 || instances.value.some(row => row.labelValue === value)) throw new Error('Choose an unused integer label value between 1 and 65535.');
    if (!name.trim()) throw new Error('An instance name is required.');
    const row: DentalInstance = { id: crypto.randomUUID(), labelValue: value, name: registry.uniqueName(name.trim()), fdi,
      color: colorAt(instances.value.length), visible: true, locked: false };
    const segmentId = mint(row); instances.value.push({ ...row, segmentId }); select(segmentId); dirty.value = true;
  }
  function updateInstance(row: EditingInstance, patch: Partial<DentalInstance>) {
    Object.assign(row, patch); registry.updateSegment(row.segmentId, { name: row.name, color: row.color, visible: row.visible, locked: row.locked }); dirty.value = true;
  }
  function setMode(value: typeof mode.value) {
    mode.value = value;
    const tools = useToolStore(); const paint = usePaintToolStore();
    if (['paint', 'erase', 'select'].includes(value)) {
      paint.setMode(value === 'erase' ? PaintMode.Erase : value === 'select' ? PaintMode.Eyedropper : PaintMode.CirclePaint);
      tools.setCurrentTool(Tools.Paint);
    } else tools.setCurrentTool(value === 'window' ? Tools.WindowLevel : Tools.Pan);
  }
  function addLandmark(position: [number, number, number]) {
    landmarks.value.push({ id: crypto.randomUUID(), name: landmarkName.value || 'Landmark', frameId: frameId.value, position: [...position] });
    landmarkName.value = `Landmark ${landmarks.value.length + 1}`; dirty.value = true;
  }
  function removeLandmark(id: string) { landmarks.value = landmarks.value.filter(p => p.id !== id); dirty.value = true; }
  function renameLandmark(id: string, name: string) { const point = landmarks.value.find(row => row.id === id); if (point) { point.name = name; dirty.value = true; } }
  function annotations(): DentalAnnotations {
    return { labelmapAssetId: labelAssetId.value, meshLabelsAssetId: null, instances: clone(instances.value.map(({ segmentId, ...row }) => row)), landmarks: clone(landmarks.value), registration: null };
  }
  function exportLabels(format = 'nii.gz') { return exportVolumeLabels(imageId.value, new Map(instances.value.map(row => [row.segmentId, row.labelValue])), format, instances.value); }
  function markSaved(assetId: string | null) { labelAssetId.value = assetId; dirty.value = false; labelsDirty.value = false; }
  return { imageId, frameId, instances, landmarks, selected, mode, landmarkName, dirty, labelsDirty, labelAssetId,
    undoCount: history.undoCount, redoCount: history.redoCount, historyNotice: history.notice,
    undo: history.undoStroke, redo: history.redoStroke, dispose: history.dispose,
    loadAnnotations, addInstance, updateInstance, select, setMode, addLandmark, removeLandmark, renameLandmark, annotations, exportLabels, markSaved };
});
